import React from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Box, Typography, Chip, Avatar, useTheme, alpha, AvatarGroup, Tooltip } from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Loop as LoopIcon,
} from '@mui/icons-material';

export interface KanbanCardData {
  id: string;
  title: string;
  value?: number | string;
  probability?: number;
  dueDate?: string;
  scheduledDate?: string;
  assignedUsers?: {
    id: string;
    name: string;
    avatar?: string;
  }[];
  assignedUser?: {
    id: number;
    name: string;
  };
  customer?: {
    id: string;
    displayName: string;
  };
  description?: string;
  priority?: string;
  category?: {
    id: string;
    name: string;
  };
  recurrenceFrequency?: string;
  recurrenceEndDate?: string;
  isVirtual?: boolean;
  [key: string]: any;
}

interface KanbanCardProps {
  item: KanbanCardData;
  index: number;
  onClick?: (item: KanbanCardData) => void;
  showValue?: boolean;
  showProbability?: boolean;
  showDueDate?: boolean;
  showCustomer?: boolean;
  showAssignedUser?: boolean;
  showPriority?: boolean;
  showCategory?: boolean;
}

const getProbabilityColor = (probability: number) => {
  if (probability >= 80) {
    return { bg: '#4caf50', text: 'white' };
  }
  if (probability >= 50) {
    return { bg: '#ff9800', text: 'white' };
  }
  if (probability >= 25) {
    return { bg: '#2196f3', text: 'white' };
  }
  return { bg: '#f44336', text: 'white' };
};

const getPriorityColor = (priority: string): 'error' | 'warning' | 'info' | 'success' | 'default' => {
  switch (priority?.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'low':
      return 'info';
    default:
      return 'default';
  }
};

const formatValue = (value: number | string | undefined, includeSymbol: boolean = true): string => {
  if (value === undefined || value === null) return includeSymbol ? '$0' : '0';

  // If it's already a string
  if (typeof value === 'string') {
    // Remove existing $ if we don't want the symbol
    const cleanValue = value.replace(/^\$/, '');

    // If it's a string number, parse it and format
    const parsed = parseFloat(cleanValue.replace(/[,$]/g, ''));
    if (!isNaN(parsed)) {
      const formatted = parsed.toLocaleString();
      return includeSymbol ? `$${formatted}` : formatted;
    }
    return includeSymbol ? `$${cleanValue}` : cleanValue;
  }

  // If it's a number, format normally
  const formattedNumber = value.toLocaleString();
  return includeSymbol ? `$${formattedNumber}` : formattedNumber;
};

export const KanbanCard: React.FC<KanbanCardProps> = ({
  item,
  index,
  onClick,
  showValue = true,
  showProbability = true,
  showDueDate = true,
  showCustomer = true,
  showAssignedUser = true,
  showPriority = false,
  showCategory = false,
}) => {
  const theme = useTheme();

  const handleClick = () => {
    if (onClick) {
      onClick(item);
    }
  };

  const probabilityColors = showProbability && item.probability
    ? getProbabilityColor(item.probability)
    : null;

  return (
    <Draggable draggableId={item.id} index={index}>
      {(provided, snapshot) => (
        <Box
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
          onClick={() => {
            if (!snapshot.isDragging) { handleClick(); }
          }}
          sx={{
            backgroundColor: snapshot.isDragging ? alpha(theme.palette.primary.main, 0.05) : 'white',
            border: snapshot.isDragging ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
            borderRadius: '10px',
            cursor: snapshot.isDragging ? 'grabbing' : 'pointer',
            boxShadow: snapshot.isDragging
              ? '0 8px 24px rgba(0,0,0,0.15)'
              : '0 2px 4px rgba(0,0,0,0.05)',
            transition: 'all 0.2s ease',
            transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderColor: alpha(theme.palette.primary.main, 0.3),
              transform: 'translateY(-2px)',
            },
          }}
        >
          <Box sx={{ p: 2.5 }}>
            {/* Header */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '0.95rem',
                lineHeight: 1.3,
                mb: 1.5,
                color: theme.palette.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {item.title}
            </Typography>

            {/* Value */}
            {showValue && item.value !== undefined && (
              <Typography
                variant="h5"
                sx={{
                  color: '#4caf50',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  letterSpacing: '-0.02em',
                  mb: 2,
                }}
              >
                {formatValue(item.value, true)}
              </Typography>
            )}

            {/* Customer */}
            {showCustomer && item.customer && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <BusinessIcon sx={{ fontSize: '1rem', color: theme.palette.text.secondary }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.customer.displayName}
                </Typography>
              </Box>
            )}

            {/* Scheduled Date/Time */}
            {item.scheduledDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <CalendarIcon sx={{ fontSize: '1rem', color: theme.palette.primary.main }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                >
                  {new Date(item.scheduledDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </Typography>
                {item.isVirtual && (
                  <Tooltip title="Recurring Task Instance">
                    <LoopIcon sx={{ fontSize: '1rem', color: theme.palette.primary.main }} />
                  </Tooltip>
                )}
              </Box>
            )}

            {/* Recurrence Info */}
            {item.scheduledDate && !item.isVirtual && item.recurrenceFrequency && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <LoopIcon sx={{ fontSize: '1rem', color: theme.palette.primary.main }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                >
                  {item.recurrenceFrequency}
                  {item.recurrenceEndDate && `, Ends ${new Date(item.recurrenceEndDate).toLocaleDateString()}`}
                </Typography>
              </Box>
            )}

            {/* Description */}
            {item.description && (
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.8rem',
                  mb: 1.5,
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {item.description}
              </Typography>
            )}

            {/* Category */}
            {showCategory && item.category && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <CategoryIcon sx={{ fontSize: '1rem', color: theme.palette.text.secondary }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                >
                  {item.category.name}
                </Typography>
              </Box>
            )}

            {/* Bottom row - Priority, Probability and Assigned User */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {showPriority && item.priority && (
                  <Chip
                    label={item.priority}
                    size="small"
                    color={getPriorityColor(item.priority)}
                    sx={{
                      fontSize: '0.75rem',
                      height: 24,
                      '& .MuiChip-label': {
                        px: 1.5,
                      },
                    }}
                  />
                )}
                {showProbability && probabilityColors && (
                  <Chip
                    label={`${item.probability || 0}%`}
                    size="small"
                    sx={{
                      backgroundColor: probabilityColors.bg,
                      color: probabilityColors.text,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 24,
                      '& .MuiChip-label': {
                        px: 1.5,
                      },
                    }}
                  />
                )}
              </Box>

              {showAssignedUser && (
                <Box onClick={(e) => e.stopPropagation()}>
                  {item.assignedUsers && item.assignedUsers.length > 0 ? (
                    <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                      {item.assignedUsers.map((user) => (
                        <Avatar
                          key={user.id}
                          src={user.avatar}
                          alt={user.name}
                          title={user.name}
                          sx={{
                            bgcolor: theme.palette.primary.main,
                          }}
                        >
                          {user.name?.charAt(0)?.toUpperCase() || <PersonIcon sx={{ fontSize: 14 }} />}
                        </Avatar>
                      ))}
                    </AvatarGroup>
                  ) : item.assignedUser ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: '0.75rem',
                          bgcolor: theme.palette.primary.main,
                        }}
                      >
                        {item.assignedUser.name?.charAt(0)?.toUpperCase() || <PersonIcon sx={{ fontSize: 14 }} />}
                      </Avatar>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.75rem',
                          color: theme.palette.text.secondary,
                          fontWeight: 500,
                        }}
                      >
                        {item.assignedUser.name}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Draggable>
  );
};